import { useMutation, useQuery } from '@tanstack/react-query';
import { signInWithCustomToken } from 'firebase/auth';
import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink, useGo } from '../components/Nav';
import { Banner, Button, ErrorText, Spinner } from '../components/ui';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import { auth } from '../lib/firebase';
import { exchangeCompInviteCode, fetchCompInvite, resendCompInvite, type CompInviteDetails } from '../queries/compInvites';

/**
 * Where the email from the Slate team lands somebody we set up ourselves:
 * an account, comped, and possibly a team of their own, made before they
 * had ever heard of us. Rendered outside the app shell, because they have
 * no library yet.
 *
 * The emailed copy of the link carries a one-time code. With it the page
 * signs them in and hands them to the welcome steps; the plan is already
 * on the account, so there is nothing else to accept. Without it, or once
 * it has been spent, the page says what they were given and offers a fresh
 * email or the ordinary sign-in.
 */

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-slate-950 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Wordmark />
        </div>
        <div className="card overflow-hidden">
          <ClapperRule />
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Working({ label }: { label: string }) {
  return (
    <Shell>
      <div className="flex items-center justify-center gap-2.5 py-6 text-chalk-faint">
        <Spinner />
        <span className="text-body">{label}</span>
      </div>
    </Shell>
  );
}

function untilLabel(until: string | null): string | null {
  if (!until) return null;
  const date = new Date(until);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

/** What they were given, in the person's own terms. */
function Given({ invite }: { invite: CompInviteDetails }) {
  const until = untilLabel(invite.until);
  const seats = invite.team ? `${invite.team.seats} ${invite.team.seats === 1 ? 'seat' : 'seats'}` : null;
  return (
    <>
      <p className="text-caption uppercase tracking-[0.08em] text-chalk-faint">On us</p>
      <h1 className="mt-2 text-h3 text-chalk">
        {invite.team ? `${invite.team.name} is set up for you` : 'Slate is on us'}
      </h1>
      <p className="mt-2 text-body text-chalk-dim">
        {invite.team ? (
          <>
            The Slate team made you a team, <span className="text-chalk">{invite.team.name}</span>, with {seats} on
            the paid plan. You are its owner: once you are in, invite people from Settings and each of them takes a
            seat.
          </>
        ) : (
          <>The Slate team put your account, <span className="text-chalk">{invite.email}</span>, on the paid plan.</>
        )}{' '}
        {until ? `It runs until ${until}.` : 'There is no expiry.'}
      </p>
    </>
  );
}

export function StartPage({ token }: { token: string }) {
  /**
   * The code from the email, read once on the first render and taken out of
   * the address bar as soon as it has been spent: a sign-in credential has
   * no business in browser history or a bookmark.
   */
  const [code] = useState(() => new URLSearchParams(window.location.search).get('code'));
  const [autoSignIn, setAutoSignIn] = useState<'idle' | 'working' | 'done' | 'failed'>(code ? 'working' : 'idle');
  const { user, loading, signOut } = useAuth();
  const go = useGo();

  useEffect(() => {
    track('Viewed Start Page', { withCode: Boolean(code) });
  }, [code]);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    void (async () => {
      try {
        const { customToken } = await exchangeCompInviteCode(token, code);
        if (cancelled) return;
        await signInWithCustomToken(auth, customToken);
        if (!cancelled) {
          track('Comp Invite Sign-In');
          setAutoSignIn('done');
        }
      } catch {
        if (!cancelled) setAutoSignIn('failed');
      } finally {
        window.history.replaceState({}, '', `/start/${token}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, token]);

  const details = useQuery({ queryKey: ['comp-invite', token], queryFn: () => fetchCompInvite(token), retry: false });
  const resend = useMutation({ mutationFn: () => resendCompInvite(token) });

  // Signed in, whether by the code just now or from before: the library,
  // and the welcome steps on the way there for an account that has not done
  // them. The plan is already on the account, so there is nothing to accept.
  const signedIn = Boolean(user) && !loading && autoSignIn !== 'working';
  useEffect(() => {
    if (signedIn) go('/', { replace: true });
  }, [go, signedIn]);

  if (loading || autoSignIn === 'working') return <Working label={code ? 'Signing you in…' : 'Checking your session…'} />;
  if (signedIn) return <Working label="Opening Slate…" />;

  if (details.isError) {
    return (
      <Shell>
        <Banner tone="danger" title="This link is not one of ours">
          {errorMessage(details.error)}
        </Banner>
        <AppLink to="/login" className="mt-5 block">
          <Button variant="primary" size="lg" className="w-full">
            Sign in to Slate
          </Button>
        </AppLink>
      </Shell>
    );
  }
  if (!details.data) return <Working label="Reading the invitation…" />;

  const invite = details.data;
  const signInHere = '/login';

  return (
    <Shell>
      <Given invite={invite} />

      {!invite.active ? (
        <div className="mt-4">
          <Banner tone="caution" title="The plan has since lapsed">
            The account is still yours, on the free plan. Reply to the email that brought you here if that is not what
            you expected.
          </Banner>
        </div>
      ) : null}

      {autoSignIn === 'failed' || (code === null && !invite.signedIn) ? (
        <div className="mt-4">
          <Banner
            tone="caution"
            title={autoSignIn === 'failed' ? 'That sign-in link has already been used' : 'Open the link from your email to get in'}
          >
            Links from the email sign you in once, so that forwarding one does not hand over the account. Ask for a
            fresh one and it will arrive at {invite.email}.
          </Banner>
          <div className="mt-3 flex items-center gap-3">
            <Button variant="primary" busy={resend.isPending} disabled={resend.isSuccess} onClick={() => resend.mutate()}>
              {resend.isSuccess ? 'Sent' : 'Email me a fresh link'}
            </Button>
            {resend.isSuccess ? <span className="text-caption text-go">Check {resend.data?.email ?? 'your inbox'}.</span> : null}
          </div>
          {resend.isError ? (
            <div className="mt-2">
              <ErrorText>{errorMessage(resend.error)}</ErrorText>
            </div>
          ) : null}
        </div>
      ) : null}

      <p className="mt-5 text-body text-chalk-dim">
        {invite.signedIn
          ? 'You have signed in before, so the ordinary door is the one to use.'
          : 'Already set a password, or use Google for this address? Sign in the ordinary way.'}{' '}
        Use <span className="text-chalk">{invite.email}</span>; the plan is on that account.
      </p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <AppLink to={signInHere} className="sm:flex-1">
          <Button variant={invite.signedIn ? 'primary' : 'secondary'} size="lg" className="w-full">
            Sign in
          </Button>
        </AppLink>
        <AppLink to={`/forgot-password?email=${encodeURIComponent(invite.email)}`} className="sm:flex-1">
          <Button variant="ghost" size="lg" className="w-full">
            Set a password
          </Button>
        </AppLink>
      </div>
      {user ? (
        <p className="mt-4 text-caption text-chalk-faint">
          Signed in as someone else?{' '}
          <button type="button" onClick={() => void signOut()} className="underline hover:text-chalk">
            Sign out
          </button>
        </p>
      ) : null}
    </Shell>
  );
}
